import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const CaseOutcomesAudit = () => {
  const [selectedFilter, setSelectedFilter] = useState('all');

  // Mock case outcomes data
  const outcomes = [
    {
      id: 'CASE-2024-089',
      caseId: 'CASE-2024-089',
      type: 'AML Review',
      customerName: 'Michael Rodriguez',
      decision: 'approved',
      assignedTo: 'Sarah Johnson',
      reviewedBy: 'David Kim',
      createdAt: '2024-11-20T09:00:00Z',
      resolvedAt: '2024-11-20T13:30:00Z',
      resolutionTime: '4.5h',
      slaStatus: 'within',
      actions: [
        { timestamp: '2024-11-20T09:00:00Z', user: 'Sarah Johnson', action: 'Case opened' },
        { timestamp: '2024-11-20T10:15:00Z', user: 'Sarah Johnson', action: 'Documents reviewed' },
        { timestamp: '2024-11-20T12:00:00Z', user: 'David Kim', action: 'Second review requested' },
        { timestamp: '2024-11-20T13:30:00Z', user: 'David Kim', action: 'Approved with conditions' }
      ],
      notes: 'Customer provided sufficient documentation. Source of funds verified. Low risk profile maintained.'
    },
    {
      id: 'CASE-2024-090',
      caseId: 'CASE-2024-090',
      type: 'Transaction Review',
      customerName: 'Zhang Wei',
      decision: 'rejected',
      assignedTo: 'David Kim',
      reviewedBy: 'Sarah Johnson',
      createdAt: '2024-11-21T14:00:00Z',
      resolvedAt: '2024-11-21T17:15:00Z',
      resolutionTime: '3.25h',
      slaStatus: 'within',
      actions: [
        { timestamp: '2024-11-21T14:00:00Z', user: 'David Kim', action: 'Case opened' },
        { timestamp: '2024-11-21T15:30:00Z', user: 'David Kim', action: 'Risk assessment completed' },
        { timestamp: '2024-11-21T17:15:00Z', user: 'Sarah Johnson', action: 'Transaction rejected' }
      ],
      notes: 'High-risk corridor. Customer unable to provide adequate source of funds documentation. Structuring patterns detected.'
    },
    {
      id: 'CASE-2024-091',
      caseId: 'CASE-2024-091',
      type: 'Document Verification',
      customerName: 'Maria Garcia',
      decision: 'pending',
      assignedTo: 'Emily Chen',
      reviewedBy: null,
      createdAt: '2024-11-26T11:00:00Z',
      resolvedAt: null,
      resolutionTime: '14h (ongoing)',
      slaStatus: 'within',
      actions: [
        { timestamp: '2024-11-26T11:00:00Z', user: 'Emily Chen', action: 'Case opened' },
        { timestamp: '2024-11-26T13:45:00Z', user: 'Emily Chen', action: 'Requested additional documents' },
        { timestamp: '2024-11-27T09:30:00Z', user: 'Emily Chen', action: 'Documents received - under review' }
      ],
      notes: 'Awaiting verification of new passport document. Customer has been responsive.'
    },
    {
      id: 'CASE-2024-087',
      caseId: 'CASE-2024-087',
      type: 'Enhanced Due Diligence',
      customerName: 'Ahmed Hassan',
      decision: 'escalated',
      assignedTo: 'Sarah Johnson',
      reviewedBy: 'Compliance Manager',
      createdAt: '2024-11-18T08:00:00Z',
      resolvedAt: '2024-11-19T16:45:00Z',
      resolutionTime: '32.75h',
      slaStatus: 'breached',
      actions: [
        { timestamp: '2024-11-18T08:00:00Z', user: 'Sarah Johnson', action: 'Case opened' },
        { timestamp: '2024-11-18T14:30:00Z', user: 'Sarah Johnson', action: 'Escalated to manager' },
        { timestamp: '2024-11-19T10:00:00Z', user: 'Compliance Manager', action: 'Legal review initiated' },
        { timestamp: '2024-11-19T16:45:00Z', user: 'Compliance Manager', action: 'Approved with enhanced monitoring' }
      ],
      notes: 'PEP match required enhanced due diligence. Legal review completed. Customer approved with ongoing monitoring.'
    }
  ];

  // Calculate SLA metrics
  const totalCases = outcomes?.length;
  const resolvedCases = outcomes?.filter(c => c?.decision !== 'pending')?.length;
  const withinSLA = outcomes?.filter(c => c?.slaStatus === 'within')?.length;
  const slaPercentage = ((withinSLA / totalCases) * 100)?.toFixed(1);
  const avgResolutionTime = '5.2h'; // Mock calculation

  const getDecisionBadge = (decision) => {
    const config = {
      approved: { bg: 'bg-success/10', text: 'text-success', label: 'Approved', icon: 'CheckCircle' },
      rejected: { bg: 'bg-error/10', text: 'text-error', label: 'Rejected', icon: 'XCircle' },
      pending: { bg: 'bg-warning/10', text: 'text-warning', label: 'Pending', icon: 'Clock' },
      escalated: { bg: 'bg-accent/10', text: 'text-accent', label: 'Escalated', icon: 'AlertTriangle' }
    };
    const badge = config?.[decision] || config?.pending;
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${badge?.bg} ${badge?.text}`}>
        <Icon name={badge?.icon} size={14} className="mr-1" />
        {badge?.label}
      </span>
    );
  };

  const getSLABadge = (status) => {
    return status === 'within' ? (
      <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-success/10 text-success">
        <Icon name="CheckCircle" size={12} className="mr-1" />
        Within SLA
      </span>
    ) : (
      <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-error/10 text-error">
        <Icon name="AlertCircle" size={12} className="mr-1" />
        SLA Breached
      </span>
    );
  };

  const filteredOutcomes = selectedFilter === 'all' 
    ? outcomes 
    : outcomes?.filter(o => o?.decision === selectedFilter);

  return (
    <div className="space-y-6">
      {/* Header & Metrics */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-foreground">Case Outcomes & Audit Reporting</h2>
            <p className="text-muted-foreground mt-1">
              Complete history and SLA compliance tracking
            </p>
          </div>
          <button className="flex items-center space-x-2 px-4 py-2 border border-border rounded-lg hover:bg-muted/50 transition-colors">
            <Icon name="Download" size={20} />
            <span>Export Audit Log</span>
          </button>
        </div>

        {/* SLA Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-muted/30 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Cases</p>
                <p className="text-2xl font-bold text-foreground mt-1">{totalCases}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Icon name="Folder" size={24} className="text-primary" />
              </div>
            </div>
          </div>
          <div className="bg-muted/30 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Resolved Cases</p>
                <p className="text-2xl font-bold text-foreground mt-1">{resolvedCases}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-success/10 flex items-center justify-center">
                <Icon name="CheckCircle" size={24} className="text-success" />
              </div>
            </div>
          </div>
          <div className="bg-muted/30 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Avg Resolution Time</p>
                <p className="text-2xl font-bold text-foreground mt-1">{avgResolutionTime}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center">
                <Icon name="Clock" size={24} className="text-accent" />
              </div>
            </div>
          </div>
          <div className="bg-muted/30 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">% Resolved Within SLA</p>
                <p className="text-2xl font-bold text-success mt-1">{slaPercentage}%</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-success/10 flex items-center justify-center">
                <Icon name="Target" size={24} className="text-success" />
              </div>
            </div>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex items-center space-x-2 mt-6">
          {['all', 'approved', 'rejected', 'pending', 'escalated']?.map((filter) => (
            <button
              key={filter}
              onClick={() => setSelectedFilter(filter)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedFilter === filter
                  ? 'bg-primary text-white' :'bg-muted/30 text-foreground hover:bg-muted/50'
              }`}
            >
              {filter?.charAt(0)?.toUpperCase() + filter?.slice(1)}
            </button>
          ))}
        </div>
      </div>
      {/* Cases List */}
      <div className="space-y-4">
        {filteredOutcomes?.map((outcome) => (
          <div key={outcome?.id} className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-lg font-semibold text-foreground">{outcome?.caseId}</h3>
                    {getDecisionBadge(outcome?.decision)}
                    {getSLABadge(outcome?.slaStatus)}
                  </div>
                  <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                    <span className="flex items-center space-x-1">
                      <Icon name="User" size={14} />
                      <span>{outcome?.customerName}</span>
                    </span>
                    <span className="flex items-center space-x-1">
                      <Icon name="Tag" size={14} />
                      <span>{outcome?.type}</span>
                    </span>
                    <span className="flex items-center space-x-1">
                      <Icon name="Clock" size={14} />
                      <span>{outcome?.resolutionTime}</span>
                    </span>
                  </div>
                </div>
              </div>

              {/* Timeline */}
              <div className="mb-4">
                <h4 className="text-sm font-medium text-foreground mb-3">Decision Timeline</h4>
                <div className="space-y-3 pl-4 border-l-2 border-border">
                  {outcome?.actions?.map((action, idx) => (
                    <div key={idx} className="relative">
                      <div className="absolute -left-[21px] w-4 h-4 rounded-full bg-primary border-2 border-background"></div>
                      <div className="text-xs text-muted-foreground">
                        {new Date(action?.timestamp)?.toLocaleString()}
                      </div>
                      <div className="text-sm text-foreground font-medium">
                        {action?.user} - {action?.action}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Notes */}
              <div className="bg-muted/30 p-4 rounded-lg">
                <h4 className="text-sm font-medium text-foreground mb-2">Case Notes</h4>
                <p className="text-sm text-muted-foreground">{outcome?.notes}</p>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                <div className="text-sm text-muted-foreground">
                  Assigned: {outcome?.assignedTo} {outcome?.reviewedBy && `→ Reviewed: ${outcome?.reviewedBy}`}
                </div>
                <button className="flex items-center space-x-2 px-3 py-1.5 border border-border rounded-lg hover:bg-muted/50 transition-colors text-sm">
                  <Icon name="Eye" size={16} />
                  <span>View Full Audit</span>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CaseOutcomesAudit;